import { format as dateFnsFormat } from "date-fns"
import { makeLocalDate } from "@flockcover/flock-svc-core-types"
import isObject from "@shared/utils/checks/isObject"
import { AddedFleetEntry, createUniqueRef } from "../../../../api/fleet"
import { FleetEntryUploadRow, FleetEntryUploadRowStructure } from "../../types"

class ValidationError extends Error {}
const throwValidationError = (error: string): never => {
  throw new ValidationError(error)
}

export const isValidCSV = (data: unknown[]): data is FleetEntryUploadRow[] => {
  const expectedKeys = Object.keys(FleetEntryUploadRowStructure)
  return (
    data.find((it) => {
      // Should be an object
      if (!isObject(it)) return true
      // There should only be keys that are in upload row
      const unmatchedKeys = Object.keys(it).find((key) => !expectedKeys.includes(key))
      if (unmatchedKeys) return true
      // Each of the values must be string or undefined
      const unMatchedValues = Object.values(it).find(
        (value: unknown) => typeof value !== `string` && value !== undefined
      )
      if (unMatchedValues) return true
      return false
    }) === undefined
  )
}

export const parseDateToISODateFromUKDate = (dateString: string) => {
  const [delimiter] = /[/.-]/g.exec(dateString) || [``]
  if (delimiter.length < 1) {
    throwValidationError(
      `One of your dates is in an invalid format. Please ensure that all dates are in the form dd/mm/yyyy and try again.`
    )
  }
  const splitDate = dateString.split(delimiter)
  const day = splitDate[0]
  const month = splitDate[1]
  const year = splitDate[2]
  return new Date(`${month}/${day}/${year}`)
}

const parseDate = (date: string | undefined) => {
  if (!date) {
    return throwValidationError(`On cover dates must be supplied for all vehicles`)
  }

  const isoDate = parseDateToISODateFromUKDate(date)
  return makeLocalDate(dateFnsFormat(isoDate, `yyy-MM-dd`))
}

export const parseCSV = (rows: FleetEntryUploadRow[]) => {
  const parsedRows: Array<Partial<AddedFleetEntry>> = []
  try {
    rows.forEach((row) => {
      if (!row[`Vehicle Registration Number`]) throw new Error(`Invalid VRN`)

      parsedRows.push({
        registrationNumber: row[`Vehicle Registration Number`],
        make: row[`Vehicle Make`],
        model: row[`Vehicle Model`],
        onCoverDate: parseDate(row[`Vehicle On Cover Date`]),
        offCoverDate: row[`Vehicle Off Cover Date`] ? parseDate(row[`Vehicle Off Cover Date`]) : null,
        uniqueRef: createUniqueRef(
          row[`Vehicle Registration Number`],
          parseDate(row[`Vehicle On Cover Date`]),
          row[`Vehicle Off Cover Date`] ? parseDate(row[`Vehicle Off Cover Date`]) : undefined
        ),
      })
    })
  } catch (ex) {
    if (ex instanceof ValidationError) {
      return ex.message
    }
    throw ex
  }
  return parsedRows
}
